import { JobCard } from '@/components/jobs'
import { DimensionBars } from '@/components/jobs/dimension-bars'
import { LegitimacyBadge } from '@/components/jobs/legitimacy-badge'
import { RedFlagsPill } from '@/components/jobs/red-flags-pill'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useChatStreaming } from '@/hooks/use-chat-streaming'
import { useCoachTyping } from '@/hooks/use-coach-typing'
import { useDocuments } from '@/hooks/use-documents'
import {
  useAnalyzeJobMatch,
  useApplyResumeStatusesForJobs,
  useExternalJob,
  useInfiniteExternalJobs,
  useMyJobMatches,
  useRecommendedJobs,
  useRetryApplyResume,
  useStartApplyResume,
} from '@/hooks/use-external-jobs'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
  useCreateJobPreferences,
  useJobPreferences,
  useUpdateJobPreferences,
} from '@/hooks/use-job-preferences'
import { useCreateJobSearch, useJobSearch, useJobSearches } from '@/hooks/use-job-searches'
import { useCreateLearningFromJobMatch, useTopics } from '@/hooks/use-learning'
import { trackEvent } from '@/lib/analytics'
import { enableLetterGrades } from '@/lib/feature-flags'
import { groupMatchesByDocumentId } from '@/lib/group-matches-by-document-id'
import {
  LEARN_ALL_BLOCKED_WHILE_GENERATING_TOOLTIP,
  hasAnyGeneratingTopics,
  jobHasGeneratingTopics,
  jobHasLearningTopics,
} from '@/lib/learning-generation-limits'
import { normalizeGlobalToPercent, scoreBandForDisplay, scoreBandStyle } from '@/lib/match-scoring'
import {
  isApplyResumeDone,
  mergeTailoredReanalysisIntoMatch,
  resolveResumeBoostPercent,
} from '@/lib/tailored-reanalysis'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import { useChatStore } from '@/stores/chat-store'
import type {
  ExternalJobListingBrief,
  ExternalJobMatchWithJob,
  ExternalJobSearchParams,
  JobSearchStatus,
  JobType,
} from '@/types'
import {
  AlertCircle,
  BookOpen,
  Bookmark,
  Briefcase,
  Building2,
  CheckCircle2,
  Clock,
  DollarSign,
  Download,
  ExternalLink,
  Loader2,
  MonitorDot,
  SortAsc,
  SortDesc,
  Sparkles,
  X,
} from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

// ============================================================
// Types
// ============================================================

type JobTab = 'all' | 'apply_now' | 'need_skills' | 'not_ready' | 'saved'

interface MatchResultData {
  overall_score: number
  skills_score?: number | undefined
  experience_score?: number | undefined
  matched_skills?: string[]
  missing_skills?: string[]
  match_summary?: string | undefined
  // Multi-dim fields. Optional so the existing local objects (constructed
  // before the rubric rolled out) keep type-checking; the detail panel
  // gracefully degrades when they're absent.
  letter_grade?: import('@/types').LetterGrade | null
  global_score?: number | null
  cv_match_score?: number | null
  north_star_score?: number | null
  comp_score?: number | null
  cultural_score?: number | null
  red_flag_score?: number | null
  legitimacy?: import('@/types').LegitimacyStatus | null
  red_flags?: import('@/types').RedFlag[] | null
  dimension_rationale?: Record<string, string> | null
}

function hasRubricData(match: MatchResultData): boolean {
  return Boolean(
    match.global_score != null ||
      match.cv_match_score != null ||
      match.north_star_score != null ||
      match.comp_score != null ||
      match.cultural_score != null ||
      match.red_flag_score != null ||
      (match.red_flags != null && match.red_flags.length > 0) ||
      match.legitimacy != null ||
      (match.dimension_rationale != null && Object.keys(match.dimension_rationale).length > 0),
  )
}

/**
 * Project an API match payload onto the local MatchResultData shape, copying
 * the legacy 0-100 fields AND the new multi-dim fields (letter_grade,
 * dimension scores, legitimacy, red_flags, dimension_rationale). The earlier
 * implementation hand-picked only the legacy fields, which silently stripped
 * letter_grade off the API response — the in-chat MatchAnalysisCard never
 * had data to switch into the letter UI even though the bundle and backend
 * both supported it. Centralised here so the 3 callsites stay consistent.
 */
function toMatchResultData(match: {
  overall_score: number
  skills_score?: number | null
  experience_score?: number | null
  matched_skills?: string[] | null
  missing_skills?: string[] | null
  match_summary?: string | null
  cv_match_score?: number | string | null
  north_star_score?: number | string | null
  comp_score?: number | string | null
  cultural_score?: number | string | null
  red_flag_score?: number | string | null
  global_score?: number | string | null
  letter_grade?: string | null
  legitimacy?: string | null
  red_flags?: Array<{ type: string; severity: 'low' | 'medium' | 'high'; note: string }> | null
  dimension_rationale?: Record<string, string> | null
}): MatchResultData {
  const num = (v: number | string | null | undefined): number | null =>
    v == null ? null : typeof v === 'string' ? Number.parseFloat(v) : v
  return {
    overall_score: match.overall_score,
    skills_score: match.skills_score ?? undefined,
    experience_score: match.experience_score ?? undefined,
    matched_skills: match.matched_skills ?? [],
    missing_skills: match.missing_skills ?? [],
    match_summary: match.match_summary ?? undefined,
    cv_match_score: num(match.cv_match_score),
    north_star_score: num(match.north_star_score),
    comp_score: num(match.comp_score),
    cultural_score: num(match.cultural_score),
    red_flag_score: num(match.red_flag_score),
    global_score: num(match.global_score),
    letter_grade: (match.letter_grade as MatchResultData['letter_grade']) ?? null,
    legitimacy: (match.legitimacy as MatchResultData['legitimacy']) ?? null,
    red_flags: match.red_flags ?? null,
    dimension_rationale: match.dimension_rationale ?? null,
  }
}

// Hard cap for custom search agent scoring runs.
const JOB_SEARCH_AGENT_MAX_SCAN_RESULTS = 20

// ============================================================
// Helpers
// ============================================================

function formatSalary(min?: number, max?: number, currency?: string): string | null {
  if (!min && !max) return null
  const curr = currency || 'USD'
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: curr,
    maximumFractionDigits: 0,
  })
  if (min && max) return `${formatter.format(min)} - ${formatter.format(max)}`
  if (min) return `From ${formatter.format(min)}`
  if (max) return `Up to ${formatter.format(max)}`
  return null
}

function formatJobTypeLabel(jobType?: JobType): string | null {
  if (!jobType) return null
  return jobType
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function buildSearchCriteriaTitle(params: ExternalJobSearchParams): string {
  const baseQuery = params.query?.trim()
  const parts: string[] = []
  if (params.location) parts.push(`in ${params.location}`)
  if (params.job_type) parts.push(`job type ${formatJobTypeLabel(params.job_type)}`)
  if (params.is_remote !== undefined) {
    parts.push(`Work Setting ${params.is_remote ? 'Remote' : 'On-site'}`)
  }
  if (params.salary_min) parts.push(`and minimum salary ${params.salary_min}`)
  const base = baseQuery && baseQuery.length > 0 ? baseQuery : 'Search Criteria'
  return parts.length > 0 ? `${base} ${parts.join(' ')}` : base
}

export function appendRollingStatus(history: string[], line: string, maxLines = 3): string[] {
  if (!line) return history
  if (history[history.length - 1] === line) return history
  return [...history, line].slice(-maxLines)
}

export function buildAgentStatusLine({
  status,
  completed,
  total,
  completedLine,
}: {
  status: JobSearchStatus | null
  completed: number | null
  total: number | null
  completedLine: string
}): string | null {
  if (!status) return null
  if (status === 'pending') return 'I am preparing your job search.'
  if (status === 'matching') {
    if (total !== null && total > 0) {
      const completedCount = completed !== null ? Math.min(total, Math.max(1, completed)) : 1
      return `I am now scoring job ${completedCount} out of ${total}.`
    }
    return 'I am now scoring your opportunities.'
  }
  if (status === 'failed') {
    return 'I ran into an issue while scoring your opportunities. Please try again.'
  }
  if (status === 'completed') return completedLine
  return null
}

// ============================================================
// Search Criteria (tag-based)
// ============================================================

function SearchCriteria({
  params,
  onParamsChange,
  resumes,
  selectedResumeId,
  onSelectedResumeIdChange,
  onGenerateMatches,
  onReset,
  isGenerating,
  shortlistCount,
}: {
  params: ExternalJobSearchParams
  onParamsChange: (params: ExternalJobSearchParams) => void
  resumes: Array<{ id: number; label: string }>
  selectedResumeId: number | null
  onSelectedResumeIdChange: (resumeId: number | null) => void
  onGenerateMatches: () => void
  onReset: () => void
  isGenerating: boolean
  shortlistCount: number
}) {
  const hasShortlist = shortlistCount > 0
  const hasActiveCriteria = Boolean(
    params.query ||
      params.location ||
      params.country ||
      params.job_type ||
      params.salary_min ||
      params.category ||
      params.is_remote !== undefined,
  )
  const canAnalyzeShortlist = hasActiveCriteria && hasShortlist
  const canAnalyzeWithResume = canAnalyzeShortlist && selectedResumeId !== null
  const boundedShortlistCount = Math.min(shortlistCount, JOB_SEARCH_AGENT_MAX_SCAN_RESULTS)
  const opportunitiesLabel = `${boundedShortlistCount} ${boundedShortlistCount === 1 ? 'Opportunity' : 'Opportunities'}`

  return (
    <Card className="mb-6">
      <CardContent className="p-5">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Step 1</p>
        <h3 className="text-base font-semibold mb-2">Set Target Role</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          <input
            type="text"
            value={params.query || ''}
            onChange={(e) => {
              const value = e.target.value
              if (value) {
                onParamsChange({ ...params, query: value })
              } else {
                const { query: _, ...rest } = params
                onParamsChange(rest)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            placeholder="Type role, skills, or keywords..."
            className="flex-1 min-w-[120px] text-sm outline-none bg-transparent py-1.5 px-2"
          />
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label
              htmlFor="inline-filter-location"
              className="text-xs font-medium text-muted-foreground mb-1 block"
            >
              Location
            </label>
            <Input
              id="inline-filter-location"
              placeholder="City or state"
              value={params.location || ''}
              onChange={(e) => {
                if (e.target.value) {
                  onParamsChange({ ...params, location: e.target.value })
                } else {
                  const { location: _, ...rest } = params
                  onParamsChange(rest)
                }
              }}
              className="h-9"
            />
          </div>
          <div>
            <label
              htmlFor="inline-filter-work-setting"
              className="text-xs font-normal text-muted-foreground mb-1 block"
            >
              Work Setting
            </label>
            <Select
              value={
                params.is_remote === true ? 'remote' : params.is_remote === false ? 'onsite' : 'any'
              }
              onValueChange={(v) => {
                if (v === 'remote') onParamsChange({ ...params, is_remote: true })
                else if (v === 'onsite') onParamsChange({ ...params, is_remote: false })
                else {
                  const { is_remote: _, ...rest } = params
                  onParamsChange(rest)
                }
              }}
            >
              <SelectTrigger id="inline-filter-work-setting" className="h-9 font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                <SelectItem value="remote">Remote</SelectItem>
                <SelectItem value="onsite">On-site</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label
              htmlFor="inline-filter-job-type"
              className="text-xs font-normal text-muted-foreground mb-1 block"
            >
              Job Type
            </label>
            <Select
              value={params.job_type || '_all'}
              onValueChange={(v) => {
                if (v === '_all') {
                  const { job_type: _, ...rest } = params
                  onParamsChange(rest)
                } else {
                  onParamsChange({ ...params, job_type: v as JobType })
                }
              }}
            >
              <SelectTrigger id="inline-filter-job-type" className="h-9 font-normal">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Any</SelectItem>
                <SelectItem value="full_time">Full Time</SelectItem>
                <SelectItem value="part_time">Part Time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label
              htmlFor="inline-filter-salary"
              className="text-xs font-medium text-muted-foreground mb-1 block"
            >
              Min Salary
            </label>
            <Input
              id="inline-filter-salary"
              type="number"
              placeholder="$50,000"
              value={params.salary_min || ''}
              onChange={(e) => {
                if (e.target.value) {
                  onParamsChange({
                    ...params,
                    salary_min: Number.parseInt(e.target.value),
                  })
                } else {
                  const { salary_min: _, ...rest } = params
                  onParamsChange(rest)
                }
              }}
              className="h-9"
            />
          </div>
        </div>
        {canAnalyzeShortlist && (
          <div className="animate-in slide-in-from-top-2 fade-in duration-300">
            <div className="mt-4">
              <p className="text-xs font-medium text-muted-foreground mb-1">Step 2</p>
              <h3 className="text-base font-normal mb-2">Select Resume for analysis</h3>
              <Select
                value={selectedResumeId !== null ? `resume_${selectedResumeId}` : '_none'}
                onValueChange={(value) => {
                  if (value === '_none') {
                    onSelectedResumeIdChange(null)
                    return
                  }
                  const resumeId = Number(value.replace('resume_', ''))
                  onSelectedResumeIdChange(Number.isFinite(resumeId) ? resumeId : null)
                }}
              >
                <SelectTrigger
                  id="inline-filter-resume-selector"
                  className="h-9 max-w-md font-normal"
                >
                  <SelectValue placeholder="Select resume" />
                </SelectTrigger>
                <SelectContent>
                  {resumes.map((resume) => (
                    <SelectItem key={resume.id} value={`resume_${resume.id}`}>
                      {resume.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={() => {
                  onGenerateMatches()
                }}
                disabled={isGenerating || !canAnalyzeWithResume}
                className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {isGenerating ? 'Analyzing Opportunities...' : `Analyze ${opportunitiesLabel}`}
              </Button>
              <button
                type="button"
                onClick={onReset}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md px-3 py-1.5 hover:bg-blue-50 dark:hover:bg-blue-950 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

type StatusTextPhase = 'idle' | 'dots' | 'typing' | 'done'

function useTypingText(text: string, animationKey?: string) {
  const [displayed, setDisplayed] = useState(text)
  const [phase, setPhase] = useState<StatusTextPhase>(text ? 'done' : 'idle')
  const cancelRef = useRef(false)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      setDisplayed(text)
      setPhase(text ? 'done' : 'idle')
      return
    }
    if (!text) {
      setPhase('idle')
      setDisplayed('')
      return
    }

    if (animationKey) {
      try {
        const lastAnimated = localStorage.getItem(animationKey)
        if (lastAnimated === text) {
          setDisplayed(text)
          setPhase('done')
          return
        }
      } catch {
        // localStorage may be unavailable; fall back to in-memory behavior.
      }
    }

    cancelRef.current = false
    setDisplayed('')
    setPhase('dots')

    const indicatorTimer = setTimeout(() => {
      if (cancelRef.current) return
      setPhase('typing')
      let i = 0
      const typeNext = () => {
        if (cancelRef.current) return
        i++
        setDisplayed(text.slice(0, i))
        if (i < text.length) setTimeout(typeNext, 22)
        else {
          setPhase('done')
          if (animationKey) {
            try {
              localStorage.setItem(animationKey, text)
            } catch {
              // localStorage may be unavailable; ignore persist failure.
            }
          }
        }
      }
      typeNext()
    }, 650)

    return () => {
      cancelRef.current = true
      clearTimeout(indicatorTimer)
    }
  }, [animationKey, text])

  return { displayed, phase }
}

function RollingAgentStatusWindow({
  lines,
  isTyping,
  animationKey,
}: {
  lines: string[]
  isTyping: boolean
  animationKey?: string
}) {
  const latestLine = lines[lines.length - 1] ?? ''
  const { displayed, phase } = useTypingText(latestLine, animationKey)

  if (lines.length === 0 && !isTyping) return null
  return (
    <div className="mt-1 space-y-1">
      {phase === 'dots' ? (
        <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
          <span
            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
            style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
          />
          <span
            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
            style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
          />
          <span
            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
            style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
          />
        </span>
      ) : displayed ? (
        <p className="text-xs text-muted-foreground">
          {displayed}
          {phase === 'typing' && (
            <span className="inline-block w-[1px] h-[10px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
          )}
        </p>
      ) : null}
      {isTyping && phase === 'done' && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-400/70" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-400/70 [animation-delay:120ms]" />
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sky-400/70 [animation-delay:240ms]" />
        </div>
      )}
    </div>
  )
}

function ResultSetStatusWindow({
  lines,
  isTyping,
  resumeName,
}: {
  lines: string[]
  isTyping: boolean
  resumeName?: string | null
}) {
  const latestLine = lines[lines.length - 1] ?? ''
  const completedPrefix =
    'I have discovered jobs from your selected filters and scored them against your resume '
  const isCompletedLine = !isTyping && latestLine.startsWith(completedPrefix)

  return (
    <>
      {!isCompletedLine && (
        <RollingAgentStatusWindow
          lines={lines}
          isTyping={isTyping}
          animationKey="opportunities:job-search-agent:last-animated-line"
        />
      )}
      {(lines.length === 0 || isCompletedLine) && !isTyping && (
        <p className="mt-1 text-xs text-muted-foreground">
          I have discovered jobs from your selected filters and scored them against your resume{' '}
          <span className="font-bold text-sky-600">{resumeName || 'resume'}</span>.
        </p>
      )}
    </>
  )
}

function ResumeSetStatusWindow({
  lines,
  isTyping,
}: {
  lines: string[]
  isTyping: boolean
}) {
  return (
    <>
      <RollingAgentStatusWindow
        lines={lines}
        isTyping={isTyping}
        animationKey="opportunities:resume-scan-agent:last-animated-line"
      />
      {lines.length === 0 && !isTyping && (
        <p className="mt-1 text-xs text-muted-foreground">
          I have discovered jobs from your resume profile and scored them against your resume.
        </p>
      )}
    </>
  )
}

function ResultSetHelpFooter({
  onRequestHumanHelp,
  disabled,
}: {
  onRequestHumanHelp: () => void
  disabled: boolean
}) {
  return (
    <div className="mt-4 border-t pt-3">
      <p className="text-sm text-muted-foreground">
        If you can&apos;t find a job match for what you&apos;re looking for,{' '}
        <Button
          type="button"
          variant="link"
          className="inline h-auto p-0 align-baseline text-blue-600"
          onClick={onRequestHumanHelp}
          disabled={disabled}
        >
          please reach out to us
        </Button>{' '}
        - a human member from the Pelgo team will assist you shortly.
      </p>
    </div>
  )
}

// ============================================================
// Match Analysis Card
// ============================================================

function MatchAnalysisCard({ matchResult }: { matchResult: MatchResultData }) {
  const normalizedGlobalPercent = normalizeGlobalToPercent(matchResult.global_score)
  const score = normalizedGlobalPercent ?? matchResult.overall_score
  const useRubricPath = enableLetterGrades && hasRubricData(matchResult)
  const scoreBand = scoreBandForDisplay(matchResult.global_score, score)
  const bandStyle = scoreBandStyle(scoreBand)
  const scoreLabel = bandStyle.label
  const scoreColor = `${bandStyle.textClass} ${bandStyle.borderClass}`
  const scoreLabelColor = bandStyle.textClass
  const matchQuality = bandStyle.quality

  const scoreBandMessage =
    score < 20
      ? 'Your match score is low for this role. You can still apply, or explore other jobs that may be a better fit.'
      : score < 50
        ? 'Your resume is a partial match for this role. There are key skills you can build to improve your chances.'
        : score < 75
          ? "You're a solid match. A few targeted skills could push you to the top of the applicant pool."
          : 'Great match. A small amount of additional learning could make you stand out further.'

  return (
    <div className="rounded-xl border p-4 space-y-4" style={{ backgroundColor: '#F0F9FF' }}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <MonitorDot className="h-5 w-5 text-gray-400" />
        <h4 className="font-semibold text-base">Match Analysis</h4>
      </div>

      {/* Score row — rubric UI when data is present, legacy otherwise */}
      {useRubricPath ? (
        <div className="flex items-start gap-3">
          <div
            className={cn('rounded-2xl border-2 px-3 py-2 text-center min-w-[72px]', scoreColor)}
          >
            <div className={cn('text-2xl font-bold leading-none', bandStyle.textClass)}>
              {score}%
            </div>
            <div className={cn('text-[11px] font-semibold mt-1', bandStyle.textClass)}>
              {scoreLabel}
            </div>
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1.5 pt-1">
            {matchResult.legitimacy ? (
              <LegitimacyBadge legitimacy={matchResult.legitimacy} />
            ) : null}
            <RedFlagsPill redFlags={matchResult.red_flags} />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className={cn('px-2.5 py-1 rounded-md border-2 font-bold text-base', scoreColor)}>
            {score}%
          </span>
          <span className={cn('font-semibold text-base', scoreLabelColor)}>{scoreLabel}</span>
        </div>
      )}
      {/* Why section */}
      <div>
        <div className="flex items-center gap-1.5 mb-1.5">
          <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
          <h5 className="text-sm font-semibold">Why this is a {matchQuality}</h5>
        </div>
        <p className="text-sm text-muted-foreground">
          {matchResult.match_summary || scoreBandMessage}
        </p>
      </div>

      {/* Per-dimension breakdown — only when scored under the new rubric */}
      {useRubricPath ? (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Bookmark className="h-3.5 w-3.5 text-muted-foreground" />
            <h5 className="text-sm font-semibold">Match breakdown</h5>
          </div>
          <DimensionBars match={matchResult} />
        </div>
      ) : null}

      {/* Skills you have */}
      {matchResult.matched_skills && matchResult.matched_skills.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600 dark:text-green-400 shrink-0" />
            <h5 className="text-sm font-semibold text-green-700 dark:text-green-400">
              Skills you have
            </h5>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {matchResult.matched_skills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Skills to develop */}
      {matchResult.missing_skills && matchResult.missing_skills.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <BookOpen className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <h5 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
              Skills to develop
            </h5>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {matchResult.missing_skills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-600 border border-amber-200 dark:border-amber-800 font-medium"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================
// Job Detail Panel / Dialog
// ============================================================

function formatTimeAgoLocal(dateStr?: string): string | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return '1 day'
  if (diffDays < 7) return `${diffDays} days`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks`
  return `${Math.floor(diffDays / 30)} months`
}

function JobDetailContent({
  job,
  isLoading,
  matchResult,
  matchError,
  atsTailoredReady,
  generateResumeStatus,
  onGenerateResume,
  onRetryGenerateResume,
  onDownloadGeneratedResume,
  generateResumeDelta,
  generateResumeError,
  analyzeResumes,
  onAnalyzeMatch,
  isAnalyzing,
  jobId,
}: {
  job: ReturnType<typeof useExternalJob>['data']
  isLoading: boolean
  matchResult: MatchResultData | null
  matchError: string | null
  atsTailoredReady: boolean
  generateResumeStatus?: string | null
  onGenerateResume?: () => void
  onRetryGenerateResume?: () => void
  onDownloadGeneratedResume?: () => void
  generateResumeDelta?: number | null
  generateResumeError?: string | null
  analyzeResumes?: { id: number; label: string }[]
  onAnalyzeMatch?: (resumeId?: number | null) => void
  isAnalyzing?: boolean
  jobId?: number | null
}) {
  // Hooks must come before early returns
  const animSeenKey = jobId != null ? `pelgo:coach_anim:${jobId}` : null
  const [animAlreadySeen] = React.useState(() => {
    if (!animSeenKey) return false
    try {
      return localStorage.getItem(animSeenKey) === '1'
    } catch {
      return false
    }
  })
  const [selectedResumeId, setSelectedResumeId] = React.useState<number | null>(
    () => analyzeResumes?.[0]?.id ?? null,
  )
  React.useEffect(() => {
    setSelectedResumeId((prev) => {
      if (prev !== null && analyzeResumes?.some((r) => r.id === prev)) return prev
      return analyzeResumes?.[0]?.id ?? null
    })
  }, [analyzeResumes])

  const showAnalyzeStrip =
    !matchResult && Boolean(onAnalyzeMatch) && Boolean(analyzeResumes?.length)
  const analyzeCoachText = showAnalyzeStrip ? 'Analyse this job to see how your resume matches' : ''
  const {
    displayed: analyzeDisplayed,
    phase: analyzePhase,
    iconKey: analyzeIconKey,
  } = useCoachTyping(analyzeCoachText, animAlreadySeen)
  const nextStepAnimSeenKey = jobId != null ? `pelgo:coach_next_anim:${jobId}` : null
  const [nextStepAnimAlreadySeen] = React.useState(() => {
    if (!nextStepAnimSeenKey) return false
    try {
      return localStorage.getItem(nextStepAnimSeenKey) === '1'
    } catch {
      return false
    }
  })

  React.useEffect(() => {
    if (analyzePhase === 'done' && animSeenKey) {
      try {
        localStorage.setItem(animSeenKey, '1')
      } catch (_) {
        // localStorage may be unavailable in private browsing
      }
    }
  }, [analyzePhase, animSeenKey])

  const navigate = useNavigate()
  const createFromJobMatch = useCreateLearningFromJobMatch()
  const { data: topicsForLearnAllLimit } = useTopics(1, 100, false)
  const [learnAllError, setLearnAllError] = React.useState<string | null>(null)
  const topicItems = topicsForLearnAllLimit?.items
  const generatingForThisJob = Boolean(jobId && jobHasGeneratingTopics(jobId, topicItems))
  const anyLessonsGenerating = hasAnyGeneratingTopics(topicItems)
  const learnAllBlockedElsewhere = anyLessonsGenerating && !generatingForThisJob
  const jobHasTopics = Boolean(jobId && jobHasLearningTopics(jobId, topicItems))
  const isGeneratingResume =
    generateResumeStatus === 'preparing' ||
    generateResumeStatus === 'generating_revision' ||
    generateResumeStatus === 'reanalyzing'
  const resumeFailed = generateResumeStatus?.startsWith('failed_') && Boolean(onRetryGenerateResume)
  const showGenerateStep = Boolean(onGenerateResume) && !atsTailoredReady && !resumeFailed
  const showLearnStep =
    Boolean(matchResult) &&
    atsTailoredReady &&
    (jobHasTopics || (matchResult?.missing_skills?.length ?? 0) > 0)
  const resumeBoostPercent =
    typeof generateResumeDelta === 'number' && generateResumeDelta > 0 ? generateResumeDelta : null
  const showTailoredResumeCoach = atsTailoredReady && Boolean(matchResult)
  const showNextStepStrip =
    Boolean(matchResult) &&
    (showGenerateStep || resumeFailed || showLearnStep || showTailoredResumeCoach)
  const nextStepCoachText = showGenerateStep
    ? 'Generate a tailored ATS resume to boost your match'
    : showLearnStep && !showTailoredResumeCoach
      ? 'Learn additional skills to further improve your resume'
      : resumeFailed
        ? 'Resume generation failed'
        : ''
  const {
    displayed: nextStepDisplayed,
    phase: nextStepPhase,
    iconKey: nextStepIconKey,
  } = useCoachTyping(nextStepCoachText, nextStepAnimAlreadySeen)

  const tailoredAnimSeenKey = jobId != null ? `pelgo:detail_tailored_anim:${jobId}` : null
  const [tailoredAnimAlreadySeen] = React.useState(() => {
    if (!tailoredAnimSeenKey) return false
    try {
      return localStorage.getItem(tailoredAnimSeenKey) === '1'
    } catch {
      return false
    }
  })
  const tailoredCoachText = showTailoredResumeCoach
    ? resumeBoostPercent != null
      ? `Tailored resume covers ${resumeBoostPercent}% more key terms`
      : 'Your tailored resume is ready'
    : ''
  const {
    displayed: tailoredDisplayed,
    phase: tailoredPhase,
    iconKey: tailoredIconKey,
  } = useCoachTyping(tailoredCoachText, tailoredAnimAlreadySeen)

  React.useEffect(() => {
    if (tailoredPhase === 'done' && tailoredAnimSeenKey) {
      try {
        localStorage.setItem(tailoredAnimSeenKey, '1')
      } catch (_) {
        // localStorage may be unavailable in private browsing
      }
    }
  }, [tailoredPhase, tailoredAnimSeenKey])

  const detailCoverageLearnSeenKey =
    jobId != null ? `pelgo:detail_coverage_learn_anim:${jobId}` : null
  const [detailCoverageLearnAlreadySeen] = React.useState(() => {
    if (!detailCoverageLearnSeenKey) return false
    try {
      return localStorage.getItem(detailCoverageLearnSeenKey) === '1'
    } catch {
      return false
    }
  })
  const learnAfterCoverageText =
    showLearnStep && showTailoredResumeCoach && tailoredPhase === 'done'
      ? 'Learn additional skills to further improve your resume'
      : ''
  const {
    displayed: learnAfterDisplayed,
    phase: learnAfterPhase,
    iconKey: learnAfterIconKey,
  } = useCoachTyping(learnAfterCoverageText, detailCoverageLearnAlreadySeen)

  React.useEffect(() => {
    if (learnAfterPhase === 'done' && detailCoverageLearnSeenKey) {
      try {
        localStorage.setItem(detailCoverageLearnSeenKey, '1')
      } catch (_) {
        // localStorage may be unavailable in private browsing
      }
    }
  }, [learnAfterPhase, detailCoverageLearnSeenKey])

  const handleLearnAll = () => {
    if (!jobId || !job || !matchResult?.missing_skills?.length) return
    setLearnAllError(null)
    createFromJobMatch.mutate(
      {
        jobPostingId: jobId,
        jobTitle: job.title || 'Unknown',
        ...(job.company ? { companyName: job.company } : {}),
        ...(job.location ? { jobLocation: job.location } : {}),
        skillGaps: matchResult.missing_skills.map((skill) => ({
          skillName: skill,
          requiredLevel: 'intermediate' as const,
        })),
      },
      {
        onSuccess: () => navigate('/learn'),
        onError: (error) => {
          setLearnAllError(
            error instanceof Error ? error.message : 'Failed to create learning topics',
          )
        },
      },
    )
  }

  React.useEffect(() => {
    if (nextStepPhase === 'done' && nextStepAnimSeenKey) {
      try {
        localStorage.setItem(nextStepAnimSeenKey, '1')
      } catch (_) {
        // localStorage may be unavailable in private browsing
      }
    }
  }, [nextStepPhase, nextStepAnimSeenKey])

  const salary = job ? formatSalary(job.salary_min, job.salary_max, job.salary_currency) : null
  const timeAgo = job ? formatTimeAgoLocal(job.posted_at || job.created_at) : null
  const jobType = job?.job_type
    ? job.job_type
        .split('_')
        .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
        .join('-')
    : null

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!job) {
    return <div className="text-center py-8 text-muted-foreground">Job not found</div>
  }

  // MatchAnalysisCard now embeds the letter-grade UI directly when the flag
  // is on and the match has been scored under the new rubric, so a separate
  // wrapper above it would render the same dimension breakdown twice.
  return (
    <div className="p-4 space-y-3">
      {matchResult && <MatchAnalysisCard matchResult={matchResult} />}

      {matchError && (
        <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{matchError}</span>
        </div>
      )}

      <div className="px-1">
        <div className="flex items-center gap-2.5 mb-2">
          {job.company_logo_url ? (
            <img
              src={job.company_logo_url}
              alt={`${job.company} logo`}
              className="h-10 w-10 rounded-md object-contain bg-gray-50 border shrink-0"
            />
          ) : (
            <div className="h-10 w-10 flex items-center justify-center rounded-md bg-gray-100 dark:bg-gray-800 shrink-0">
              <Building2 className="h-5 w-5 text-gray-400" />
            </div>
          )}
          <span className="text-sm text-muted-foreground font-medium">
            {job.company || 'Company'}
          </span>
        </div>

        <h2 className="text-xl font-bold leading-snug mb-1">{job.title}</h2>

        {job.location && <p className="text-sm text-muted-foreground mb-3">{job.location}</p>}

        <div className="flex flex-col gap-1.5 text-sm text-muted-foreground mb-4">
          {salary && (
            <span className="flex items-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5 shrink-0" />
              {salary}
              {job.salary_period && <span>/ {job.salary_period}</span>}
            </span>
          )}
          {jobType && (
            <span className="flex items-center gap-1.5">
              <Briefcase className="h-3.5 w-3.5 shrink-0" />
              {jobType}
            </span>
          )}
          {timeAgo && (
            <span className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              {timeAgo}
            </span>
          )}
        </div>

        <hr className="mb-4" />

        <div className="text-sm text-muted-foreground prose prose-sm max-w-none dark:prose-invert">
          <ReactMarkdown>{job.description || ''}</ReactMarkdown>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 -mx-4 px-4 pb-3 pt-2 border-t border-border/60 bg-background/95 supports-[backdrop-filter]:bg-background/85 backdrop-blur space-y-3">
        {showAnalyzeStrip && analyzeResumes && (
          <div className="rounded-xl p-3 border bg-sky-100/60 dark:bg-sky-950/15 border-sky-300/70 dark:border-sky-800/60">
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <Sparkles
                  key={analyzeIconKey}
                  className="h-3.5 w-3.5 text-sky-500 shrink-0"
                  style={
                    analyzePhase === 'dots'
                      ? {
                          animationName: 'spin',
                          animationDuration: '0.55s',
                          animationTimingFunction: 'ease-in-out',
                          animationIterationCount: 1,
                          animationFillMode: 'none',
                        }
                      : undefined
                  }
                />
                {analyzePhase === 'dots' ? (
                  <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                    <span
                      className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                      style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                    />
                    <span
                      className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                      style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                    />
                    <span
                      className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                      style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                    />
                  </span>
                ) : (
                  <span className="text-xs text-foreground/80 min-w-0">
                    {analyzeDisplayed}
                    {analyzePhase === 'typing' && (
                      <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                    )}
                  </span>
                )}
              </div>
              {(analyzePhase === 'typing' || analyzePhase === 'done') && (
                <div className="flex items-center gap-2 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                  <Select
                    value={selectedResumeId !== null ? String(selectedResumeId) : ''}
                    onValueChange={(v) => setSelectedResumeId(Number(v))}
                    disabled={Boolean(isAnalyzing)}
                  >
                    <SelectTrigger className="h-7 text-xs flex-1 min-w-0 border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30">
                      <SelectValue placeholder="Select resume" />
                    </SelectTrigger>
                    <SelectContent>
                      {analyzeResumes.map((r) => (
                        <SelectItem key={r.id} value={String(r.id)}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs shrink-0 border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40"
                    onClick={() => onAnalyzeMatch?.(selectedResumeId)}
                    disabled={selectedResumeId === null || isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                        Analyzing...
                      </>
                    ) : (
                      'Analyse Match'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {showNextStepStrip && (
          <div className="rounded-xl p-3 border bg-sky-50/60 dark:bg-sky-950/15 border-sky-200/70 dark:border-sky-800/60">
            {showTailoredResumeCoach ? (
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-0.5">
                  {tailoredPhase === 'done' ? (
                    <div className="flex items-center justify-between gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-400">
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Sparkles
                            key={tailoredIconKey}
                            className="h-3.5 w-3.5 text-sky-500 shrink-0"
                          />
                          <span className="text-xs font-semibold text-foreground min-w-0">
                            {resumeBoostPercent != null
                              ? (() => {
                                  const pct = `${resumeBoostPercent}%`
                                  const idx = tailoredDisplayed.indexOf(pct)
                                  if (idx === -1) return <>{tailoredDisplayed}</>
                                  return (
                                    <>
                                      {tailoredDisplayed.slice(0, idx)}
                                      <span className="text-green-600 dark:text-green-400">
                                        {pct}
                                      </span>
                                      {tailoredDisplayed.slice(idx + pct.length)}
                                    </>
                                  )
                                })()
                              : tailoredDisplayed}
                          </span>
                        </div>
                        {resumeBoostPercent != null && (
                          <p className="ml-5 text-xs text-muted-foreground leading-snug">
                            We've optimized the resume to highlight transferable experience matching
                            this role.
                          </p>
                        )}
                      </div>
                      {onDownloadGeneratedResume && (
                        <Button
                          size="sm"
                          className="h-7 shrink-0 self-center text-xs bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                          onClick={onDownloadGeneratedResume}
                        >
                          <Download className="mr-1 h-3 w-3" />
                          Download Tailored Resume
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Sparkles
                        key={tailoredIconKey}
                        className="h-3.5 w-3.5 text-sky-500 shrink-0"
                        style={
                          tailoredPhase === 'dots'
                            ? {
                                animationName: 'spin',
                                animationDuration: '0.55s',
                                animationTimingFunction: 'ease-in-out',
                                animationIterationCount: 1,
                                animationFillMode: 'none',
                              }
                            : undefined
                        }
                      />
                      {tailoredPhase === 'dots' ? (
                        <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                          <span
                            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                            style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                            style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                          />
                          <span
                            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                            style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                          />
                        </span>
                      ) : (
                        <span className="text-xs font-semibold text-foreground min-w-0">
                          {resumeBoostPercent != null
                            ? (() => {
                                const pct = `${resumeBoostPercent}%`
                                const idx = tailoredDisplayed.indexOf(pct)
                                if (idx === -1) return <>{tailoredDisplayed}</>
                                return (
                                  <>
                                    {tailoredDisplayed.slice(0, idx)}
                                    <span className="text-green-600 dark:text-green-400">
                                      {pct}
                                    </span>
                                    {tailoredDisplayed.slice(idx + pct.length)}
                                  </>
                                )
                              })()
                            : tailoredDisplayed}
                          {tailoredPhase === 'typing' && (
                            <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {learnAfterCoverageText && (
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-sky-200/50 dark:border-sky-700/40">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Sparkles
                        key={learnAfterIconKey}
                        className="h-3.5 w-3.5 text-sky-500 shrink-0"
                        style={
                          learnAfterPhase === 'dots'
                            ? {
                                animationName: 'spin',
                                animationDuration: '0.55s',
                                animationTimingFunction: 'ease-in-out',
                                animationIterationCount: 1,
                                animationFillMode: 'none',
                              }
                            : undefined
                        }
                      />
                      {learnAfterPhase === 'dots' ? (
                        <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                          <span
                            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                            style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                          />
                          <span
                            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                            style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                          />
                          <span
                            className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                            style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                          />
                        </span>
                      ) : (
                        <span className="text-xs text-foreground/80 min-w-0">
                          {learnAfterDisplayed}
                          {learnAfterPhase === 'typing' && (
                            <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                          )}
                        </span>
                      )}
                    </div>

                    {(learnAfterPhase === 'typing' || learnAfterPhase === 'done') && (
                      <span
                        className={cn(
                          'shrink-0',
                          learnAllBlockedElsewhere &&
                            !jobHasTopics &&
                            !createFromJobMatch.isPending &&
                            !generatingForThisJob &&
                            'inline-flex cursor-not-allowed',
                        )}
                        title={
                          learnAllBlockedElsewhere &&
                          !jobHasTopics &&
                          !createFromJobMatch.isPending &&
                          !generatingForThisJob
                            ? LEARN_ALL_BLOCKED_WHILE_GENERATING_TOOLTIP
                            : undefined
                        }
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
                          onClick={() => {
                            if (generatingForThisJob || createFromJobMatch.isPending) return
                            if (jobHasTopics && !generatingForThisJob) {
                              navigate('/learn')
                              return
                            }
                            handleLearnAll()
                          }}
                          disabled={
                            createFromJobMatch.isPending ||
                            generatingForThisJob ||
                            (learnAllBlockedElsewhere && !jobHasTopics)
                          }
                        >
                          {createFromJobMatch.isPending || generatingForThisJob ? (
                            <>
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Curating lessons
                            </>
                          ) : jobHasTopics ? (
                            <>
                              <BookOpen className="mr-1 h-3 w-3" />
                              Continue learning
                            </>
                          ) : (
                            <>
                              <BookOpen className="mr-1 h-3 w-3" />
                              Learn all skills
                            </>
                          )}
                        </Button>
                      </span>
                    )}
                  </div>
                )}

                {learnAllError && <p className="text-xs text-red-600">{learnAllError}</p>}
                {generateResumeError && (
                  <p className="text-xs text-red-600 mt-1.5">{generateResumeError}</p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Sparkles
                    key={nextStepIconKey}
                    className="h-3.5 w-3.5 text-sky-500 shrink-0"
                    style={
                      nextStepPhase === 'dots'
                        ? {
                            animationName: 'spin',
                            animationDuration: '0.55s',
                            animationTimingFunction: 'ease-in-out',
                            animationIterationCount: 1,
                            animationFillMode: 'none',
                          }
                        : undefined
                    }
                  />
                  {nextStepPhase === 'dots' ? (
                    <span className="flex gap-[3px] items-center h-4" aria-hidden="true">
                      <span
                        className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                        style={{ animationDuration: '0.8s', animationDelay: '0ms' }}
                      />
                      <span
                        className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                        style={{ animationDuration: '0.8s', animationDelay: '160ms' }}
                      />
                      <span
                        className="w-1 h-1 rounded-full bg-sky-400 animate-bounce"
                        style={{ animationDuration: '0.8s', animationDelay: '320ms' }}
                      />
                    </span>
                  ) : (
                    <span className="text-xs text-foreground/80 min-w-0">
                      {nextStepDisplayed}
                      {nextStepPhase === 'typing' && (
                        <span className="inline-block w-[1px] h-[11px] bg-sky-400 ml-[1px] align-middle animate-pulse" />
                      )}
                    </span>
                  )}
                </div>

                {(nextStepPhase === 'typing' || nextStepPhase === 'done') && (
                  <div className="flex items-center justify-between gap-3 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300">
                    {showGenerateStep && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs shrink-0 border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40"
                        onClick={onGenerateResume}
                        disabled={isGeneratingResume}
                      >
                        {isGeneratingResume ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin mr-1" />
                            {generateResumeStatus === 'reanalyzing'
                              ? 'Reanalyzing...'
                              : 'Generating...'}
                          </>
                        ) : (
                          'Generate resume'
                        )}
                      </Button>
                    )}

                    {resumeFailed && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={onRetryGenerateResume}
                      >
                        Retry
                      </Button>
                    )}

                    {showLearnStep && (
                      <span
                        className={cn(
                          'shrink-0',
                          learnAllBlockedElsewhere &&
                            !jobHasTopics &&
                            !createFromJobMatch.isPending &&
                            !generatingForThisJob &&
                            'inline-flex cursor-not-allowed',
                        )}
                        title={
                          learnAllBlockedElsewhere &&
                          !jobHasTopics &&
                          !createFromJobMatch.isPending &&
                          !generatingForThisJob
                            ? LEARN_ALL_BLOCKED_WHILE_GENERATING_TOOLTIP
                            : undefined
                        }
                      >
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs border-sky-300 dark:border-sky-700 bg-white dark:bg-sky-950/30 hover:bg-sky-50 dark:hover:bg-sky-900/40"
                          onClick={() => {
                            if (generatingForThisJob || createFromJobMatch.isPending) return
                            if (jobHasTopics && !generatingForThisJob) {
                              navigate('/learn')
                              return
                            }
                            handleLearnAll()
                          }}
                          disabled={
                            createFromJobMatch.isPending ||
                            generatingForThisJob ||
                            (learnAllBlockedElsewhere && !jobHasTopics)
                          }
                        >
                          {createFromJobMatch.isPending || generatingForThisJob ? (
                            <>
                              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                              Curating lessons
                            </>
                          ) : jobHasTopics ? (
                            <>
                              <BookOpen className="mr-1 h-3 w-3" />
                              Continue learning
                            </>
                          ) : (
                            <>
                              <BookOpen className="mr-1 h-3 w-3" />
                              Learn all skills
                            </>
                          )}
                        </Button>
                      </span>
                    )}
                  </div>
                )}
                {learnAllError && <p className="text-xs text-red-600">{learnAllError}</p>}
                {generateResumeError && (
                  <p className="text-xs text-red-600 mt-1.5">{generateResumeError}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function useJobDetailState(jobId: number | null, initialMatchData?: MatchResultData | null) {
  const { data: job, isLoading } = useExternalJob(jobId || 0)
  const [matchResult, setMatchResult] = useState<MatchResultData | null>(initialMatchData || null)
  const [matchError] = useState<string | null>(null)

  React.useEffect(() => {
    setMatchResult(initialMatchData || null)
  }, [initialMatchData])

  return { job, isLoading, matchResult, matchError }
}

function JobDetailPanel({
  jobId,
  open,
  onClose,
  initialMatchData,
  onAnalyzeMatch,
  isAnalyzing,
  atsTailoredReady,
  onGenerateResume,
  onRetryGenerateResume,
  onDownloadGeneratedResume,
  generateResumeStatus,
  generateResumeDelta,
  generateResumeError,
  analyzeResumes,
}: {
  jobId: number | null
  open: boolean
  onClose: () => void
  initialMatchData?: MatchResultData | null
  onAnalyzeMatch: (jobId: number, resumeId?: number | null) => void
  isAnalyzing: boolean
  atsTailoredReady: boolean
  onGenerateResume?: (jobId: number) => void
  onRetryGenerateResume?: (jobId: number) => void
  onDownloadGeneratedResume?: (jobId: number) => void
  generateResumeStatus?: string | null
  generateResumeDelta?: number | null
  generateResumeError?: string | null
  analyzeResumes?: { id: number; label: string }[]
}) {
  const { job, isLoading, matchResult, matchError } = useJobDetailState(jobId, initialMatchData)

  const applyResumeDone =
    generateResumeStatus === 'ready' || generateResumeStatus === 'ready_reanalysis_failed'

  return (
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="absolute inset-0 z-40 bg-black/20"
          onClick={onClose}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />
      )}

      {/* Slide-in panel */}
      <div
        className={cn(
          'absolute inset-y-0 right-0 z-50 w-3/4 flex flex-col bg-white dark:bg-gray-900 border-l border-t shadow-xl rounded-tl-2xl overflow-hidden transition-transform duration-300 ease-in-out',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 rounded-md p-1.5 opacity-80 hover:opacity-100 hover:bg-muted transition-all bg-white dark:bg-gray-900"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <div className="flex-1 overflow-y-auto">
          <JobDetailContent
            job={job}
            isLoading={isLoading}
            matchResult={matchResult}
            matchError={matchError}
            atsTailoredReady={atsTailoredReady}
            jobId={jobId}
            isAnalyzing={isAnalyzing}
            {...(analyzeResumes ? { analyzeResumes } : {})}
            {...(jobId != null
              ? { onAnalyzeMatch: (resumeId) => onAnalyzeMatch(jobId, resumeId) }
              : {})}
            {...(generateResumeStatus != null ? { generateResumeStatus } : {})}
            {...(jobId != null && onGenerateResume
              ? { onGenerateResume: () => onGenerateResume(jobId) }
              : {})}
            {...(jobId != null && onRetryGenerateResume
              ? { onRetryGenerateResume: () => onRetryGenerateResume(jobId) }
              : {})}
            {...(jobId != null && onDownloadGeneratedResume
              ? { onDownloadGeneratedResume: () => onDownloadGeneratedResume(jobId) }
              : {})}
            generateResumeDelta={generateResumeDelta ?? null}
            {...(generateResumeError != null ? { generateResumeError } : {})}
          />
        </div>

        {/* Bottom action bar */}
        {job && (
          <div className="shrink-0 border-t px-4 py-3 flex items-center gap-2 bg-background">
            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" asChild>
              <a href={job.source_url} target="_blank" rel="noopener noreferrer">
                Apply <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
            {applyResumeDone && onDownloadGeneratedResume && !(atsTailoredReady && matchResult) && (
              <Button variant="outline" size="sm" onClick={() => onDownloadGeneratedResume(job.id)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download tailored resume
              </Button>
            )}
            {!matchResult && isAnalyzing && (
              <Button variant="outline" size="sm" disabled>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                Analysing...
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function JobDetailDialog({
  jobId,
  open,
  onOpenChange,
  initialMatchData,
  onAnalyzeMatch,
  isAnalyzing,
  atsTailoredReady,
  onGenerateResume,
  onRetryGenerateResume,
  onDownloadGeneratedResume,
  generateResumeStatus,
  generateResumeDelta,
  generateResumeError,
  analyzeResumes,
}: {
  jobId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
  initialMatchData?: MatchResultData | null
  onAnalyzeMatch: (jobId: number, resumeId?: number | null) => void
  isAnalyzing: boolean
  atsTailoredReady: boolean
  onGenerateResume?: (jobId: number) => void
  onRetryGenerateResume?: (jobId: number) => void
  onDownloadGeneratedResume?: (jobId: number) => void
  generateResumeStatus?: string | null
  generateResumeDelta?: number | null
  generateResumeError?: string | null
  analyzeResumes?: { id: number; label: string }[]
}) {
  const { job, isLoading, matchResult, matchError } = useJobDetailState(jobId, initialMatchData)

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen)
  }

  const applyResumeDone =
    generateResumeStatus === 'ready' || generateResumeStatus === 'ready_reanalysis_failed'

  if (!open || !jobId) return null

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl h-[90vh] p-0 overflow-hidden flex flex-col w-[calc(100%-2rem)] [&>button]:hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b bg-background shrink-0">
          <Button
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={!job}
            asChild={!!job}
          >
            {job ? (
              <a href={job.source_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Apply
              </a>
            ) : (
              <span>
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Apply
              </span>
            )}
          </Button>
          {applyResumeDone &&
            onDownloadGeneratedResume &&
            job &&
            !(atsTailoredReady && matchResult) && (
              <Button variant="outline" size="sm" onClick={() => onDownloadGeneratedResume(job.id)}>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download tailored resume
              </Button>
            )}
          {!matchResult && isAnalyzing && (
            <Button variant="outline" size="sm" disabled>
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              Analysing...
            </Button>
          )}
          <div className="flex-1" />
          <DialogClose className="rounded-md p-1.5 opacity-80 hover:opacity-100 hover:bg-muted transition-all">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </div>
        <div className="flex-1 overflow-y-auto">
          <JobDetailContent
            job={job}
            isLoading={isLoading}
            matchResult={matchResult}
            matchError={matchError}
            atsTailoredReady={atsTailoredReady}
            jobId={jobId}
            isAnalyzing={isAnalyzing}
            {...(analyzeResumes ? { analyzeResumes } : {})}
            {...(jobId != null
              ? { onAnalyzeMatch: (resumeId) => onAnalyzeMatch(jobId, resumeId) }
              : {})}
            {...(generateResumeStatus != null ? { generateResumeStatus } : {})}
            {...(jobId != null && onGenerateResume
              ? { onGenerateResume: () => onGenerateResume(jobId) }
              : {})}
            {...(jobId != null && onRetryGenerateResume
              ? { onRetryGenerateResume: () => onRetryGenerateResume(jobId) }
              : {})}
            {...(jobId != null && onDownloadGeneratedResume
              ? { onDownloadGeneratedResume: () => onDownloadGeneratedResume(jobId) }
              : {})}
            generateResumeDelta={generateResumeDelta ?? null}
            {...(generateResumeError != null ? { generateResumeError } : {})}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Main Page
// ============================================================

export function OpportunitiesPage() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  const initialTab = (urlParams.get('tab') as JobTab) || 'all'
  const initialSort = (urlParams.get('sort') as 'none' | 'score_desc' | 'score_asc') || 'none'
  const resumeIdFilter = urlParams.get('resume_id') ? Number(urlParams.get('resume_id')) : null
  const jobSearchFilter = urlParams.get('job_search_id')
    ? Number(urlParams.get('job_search_id'))
    : null
  const selectedMatchedResumeId = resumeIdFilter
  const selectedJobSearchId = jobSearchFilter

  const [searchParams, setSearchParams] = useState<ExternalJobSearchParams>({})
  const [selectedAnalyzeResumeId, setSelectedAnalyzeResumeId] = useState<number | null>(null)
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null)
  const [selectedMatchData, setSelectedMatchData] = useState<MatchResultData | null>(null)
  const [analyzingJobIds, setAnalyzingJobIds] = useState<Set<number>>(new Set())
  const [activeTab, setActiveTab] = useState<JobTab>(initialTab)
  const [analyzedJobs, setAnalyzedJobs] = useState<Record<number, MatchResultData>>({})
  const [generatedPreviewJobsBySearchId, setGeneratedPreviewJobsBySearchId] = useState<
    Record<number, ExternalJobListingBrief[]>
  >({})
  const [sortOrder, setSortOrder] = useState<'none' | 'score_desc' | 'score_asc'>(initialSort)
  const [learnAllPendingJobId, setLearnAllPendingJobId] = useState<number | null>(null)
  const [learnAllListError, setLearnAllListError] = useState<{
    jobId: number
    message: string
  } | null>(null)
  const [customSearchStatusLines, setCustomSearchStatusLines] = useState<string[]>([])
  const [resumeStatusLines, setResumeStatusLines] = useState<string[]>([])
  const preferenceHydratedRef = useRef(false)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previousRouteRef = useRef<string | null>(null)
  const lastCustomStatusSignatureRef = useRef<string | null>(null)
  const lastResumeStatusSignatureRef = useRef<string | null>(null)

  const { chatId } = useChatStore()
  const { sendMessageStreaming } = useChatStreaming()
  const { data: documents } = useDocuments()
  const { data: topicsData } = useTopics(1, 100, false)
  const { data: preferences } = useJobPreferences()
  const createPreferences = useCreateJobPreferences()
  const updatePreferences = useUpdateJobPreferences()
  const sentinelRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useInfiniteExternalJobs(searchParams)
  const { data: myMatches } = useMyJobMatches(0, 100)
  const { data: recommended } = useRecommendedJobs(50)
  const analyzeMatch = useAnalyzeJobMatch()
  const startApplyResume = useStartApplyResume()
  const retryApplyResume = useRetryApplyResume()
  const createFromJobMatch = useCreateLearningFromJobMatch()
  const createJobSearch = useCreateJobSearch()
  const { data: jobSearchesData } = useJobSearches(0, 20)
  const { data: selectedJobSearchData } = useJobSearch(selectedJobSearchId, 0, 100)

  const applyResumeJobIds = useMemo(() => {
    const pages = data?.pages.flatMap((page) => page.items) || []
    const seen = new Set<number>()
    const out: number[] = []
    for (const j of pages) {
      if (!seen.has(j.id)) {
        seen.add(j.id)
        out.push(j.id)
      }
    }
    for (const match of recommended?.items ?? []) {
      if (resumeIdFilter && match.document_id !== resumeIdFilter) continue
      const jl = match.job_listing
      if (jl && !seen.has(jl.id)) {
        seen.add(jl.id)
        out.push(jl.id)
      }
    }
    for (const match of myMatches?.items ?? []) {
      if (resumeIdFilter && match.document_id !== resumeIdFilter) continue
      const jl = match.job_listing
      if (jl && !seen.has(jl.id)) {
        seen.add(jl.id)
        out.push(jl.id)
      }
    }
    for (const jobs of Object.values(generatedPreviewJobsBySearchId)) {
      for (const j of jobs) {
        if (!seen.has(j.id)) {
          seen.add(j.id)
          out.push(j.id)
        }
      }
    }
    for (const match of selectedJobSearchData?.matches ?? []) {
      const id = match.job_listing_id
      if (id && !seen.has(id)) {
        seen.add(id)
        out.push(id)
      }
    }
    if (selectedJobId != null && !seen.has(selectedJobId)) {
      seen.add(selectedJobId)
      out.push(selectedJobId)
    }
    return out
  }, [
    data?.pages,
    recommended?.items,
    myMatches?.items,
    resumeIdFilter,
    generatedPreviewJobsBySearchId,
    selectedJobSearchData?.matches,
    selectedJobId,
  ])

  const { runsByJobId: applyResumeRunsByJobId } = useApplyResumeStatusesForJobs(applyResumeJobIds)

  const selectedJobApplyRun =
    selectedJobId != null ? applyResumeRunsByJobId[selectedJobId] : undefined
  const selectedJobAtsBoostPercent = resolveResumeBoostPercent(selectedJobApplyRun)
  const detailAtsTailoredReady = isApplyResumeDone(selectedJobApplyRun)

  const detailPanelMatchData = useMemo(() => {
    if (selectedJobId == null) return selectedMatchData
    const base = analyzedJobs[selectedJobId] ?? selectedMatchData
    return mergeTailoredReanalysisIntoMatch(base, selectedJobApplyRun)
  }, [selectedJobId, analyzedJobs, selectedMatchData, selectedJobApplyRun])

  const customSearchSets = useMemo(
    () => (jobSearchesData?.items ?? []).filter((search) => search.source_type === 'custom_search'),
    [jobSearchesData],
  )

  const mobileJobSearchTabTargetId = useMemo(() => {
    if (customSearchSets.length === 0) return null
    if (
      selectedJobSearchId !== null &&
      customSearchSets.some((search) => search.id === selectedJobSearchId)
    ) {
      return selectedJobSearchId
    }
    const processing = customSearchSets.find(
      (search) => search.status === 'pending' || search.status === 'matching',
    )
    if (processing) return processing.id
    return customSearchSets[0]?.id ?? null
  }, [customSearchSets, selectedJobSearchId])

  const jobIdsWithLearningPath = useMemo(() => {
    const set = new Set<number>()
    for (const topic of topicsData?.items ?? []) {
      if (topic.job_posting_id != null) {
        set.add(topic.job_posting_id)
      }
    }
    return set
  }, [topicsData])

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage()
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(handleObserver, { rootMargin: '200px' })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleObserver])

  // Populate analyzedJobs from fetched matches (filtered by resume if specified)
  useEffect(() => {
    if (myMatches?.items) {
      const matchesMap: Record<number, MatchResultData> = {}
      for (const match of myMatches.items) {
        if (resumeIdFilter && match.document_id !== resumeIdFilter) continue
        matchesMap[match.job_listing_id] = toMatchResultData(match)
      }
      setAnalyzedJobs((prev) => ({ ...matchesMap, ...prev }))
    }
  }, [myMatches, resumeIdFilter])

  // Also add recommended jobs to analyzedJobs
  useEffect(() => {
    if (recommended?.items) {
      const recMap: Record<number, MatchResultData> = {}
      for (const match of recommended.items) {
        if (resumeIdFilter && match.document_id !== resumeIdFilter) continue
        if (match.overall_score > 0) {
          recMap[match.job_listing_id] = toMatchResultData(match)
        }
      }
      setAnalyzedJobs((prev) => ({ ...recMap, ...prev }))
    }
  }, [recommended, resumeIdFilter])

  const handleViewDetails = useCallback(
    (jobId: number) => {
      setSelectedJobId(jobId)
      setSelectedMatchData(analyzedJobs[jobId] || null)
    },
    [analyzedJobs],
  )

  const handleAnalyzeMatch = useCallback(
    async (jobId: number, resumeId?: number | null) => {
      setAnalyzingJobIds((prev) => {
        const next = new Set(prev)
        next.add(jobId)
        return next
      })
      try {
        const result = await analyzeMatch.mutateAsync({
          jobId,
          chatId,
          ...(resumeId ? { request: { document_id: resumeId } } : {}),
        })
        const matchData = toMatchResultData(result)
        setAnalyzedJobs((prev) => ({ ...prev, [jobId]: matchData }))
        setSelectedJobId(jobId)
        setSelectedMatchData(matchData)
      } finally {
        setAnalyzingJobIds((prev) => {
          const next = new Set(prev)
          next.delete(jobId)
          return next
        })
      }
    },
    [analyzeMatch, chatId],
  )

  const handleAskAbout = useCallback(
    (jobId: number) => {
      if (!chatId) return
      const job = allJobsRef.current.find((j) => j.id === jobId)
      if (!job) return
      const title = job.title || 'this job'
      const company = job.company ? ` at ${job.company}` : ''
      sendMessageStreaming(
        `Tell me about the "${title}"${company} position. Is it a good fit for me?`,
        undefined,
        undefined,
        { job_id: jobId },
      )
    },
    [chatId, sendMessageStreaming],
  )

  const handleGenerateResume = useCallback(
    async (jobId: number) => {
      await startApplyResume.mutateAsync(jobId)
    },
    [startApplyResume],
  )

  const handleRetryGenerateResume = useCallback(
    async (jobId: number) => {
      await retryApplyResume.mutateAsync(jobId)
    },
    [retryApplyResume],
  )

  const handleDownloadGeneratedResume = useCallback(
    async (jobId: number) => {
      const run = applyResumeRunsByJobId[jobId]
      if (!run?.revision_id || !run.base_document_id) return

      const baseDocument = documents?.find((doc) => doc.id === run.base_document_id)
      const baseName = (baseDocument?.original_filename || 'resume').replace(/\.[^/.]+$/, '')
      const job = allJobsRef.current.find((j) => j.id === jobId)
      const jobTitle = (job?.title || 'job').trim()
      const safePart = (val: string) =>
        val
          .replace(/[<>:"/\\|?*]/g, '_')
          .split('')
          .filter((ch) => ch.charCodeAt(0) >= 32)
          .join('')
          .replace(/\s+/g, ' ')
          .trim()
      const filename = `${safePart(baseName)}_${safePart(jobTitle)}.pdf`
      const { download_url } = await documentService.getRevisionDownloadUrl(
        run.base_document_id,
        run.revision_id,
        'pdf',
        filename,
      )
      window.open(download_url, '_blank')
    },
    [applyResumeRunsByJobId, documents],
  )

  const handleViewGeneratedResume = useCallback(() => {
    navigate('/resume')
  }, [navigate])

  const handleRequestHumanCoach = useCallback(() => {
    if (!chatId) return
    const coachHelpPrompt = "I'd like the help of a human coach to find better matches"
    if (isMobile) {
      sessionStorage.setItem('pending_coach_message', JSON.stringify({ message: coachHelpPrompt }))
      navigate('/chat')
      return
    }
    sendMessageStreaming(coachHelpPrompt)
  }, [chatId, isMobile, navigate, sendMessageStreaming])

  const handleLearnAllFromJob = useCallback(
    (job: ExternalJobListingBrief, match: MatchResultData) => {
      if (!match.missing_skills?.length) return
      setLearnAllListError(null)
      setLearnAllPendingJobId(job.id)
      createFromJobMatch.mutate(
        {
          jobPostingId: job.id,
          jobTitle: job.title || 'Unknown',
          ...(job.company ? { companyName: job.company } : {}),
          ...(job.location ? { jobLocation: job.location } : {}),
          skillGaps: match.missing_skills.map((skill) => ({
            skillName: skill,
            requiredLevel: 'intermediate' as const,
          })),
        },
        {
          onSuccess: () => {
            setLearnAllPendingJobId(null)
            navigate('/learn')
          },
          onError: (error) => {
            setLearnAllPendingJobId(null)
            setLearnAllListError({
              jobId: job.id,
              message: error instanceof Error ? error.message : 'Failed to create learning topics',
            })
          },
        },
      )
    },
    [createFromJobMatch, navigate],
  )

  const allJobs = data?.pages.flatMap((page) => page.items) || []

  // Build a combined job list: allJobs + matched jobs not already in allJobs
  const allJobsWithMatches = useMemo(() => {
    const seenIds = new Set(allJobs.map((j) => j.id))
    const extra: ExternalJobListingBrief[] = []
    for (const match of recommended?.items ?? []) {
      if (resumeIdFilter && match.document_id !== resumeIdFilter) continue
      if (match.job_listing && !seenIds.has(match.job_listing.id)) {
        seenIds.add(match.job_listing.id)
        extra.push(match.job_listing)
      }
    }
    for (const match of myMatches?.items ?? []) {
      if (resumeIdFilter && match.document_id !== resumeIdFilter) continue
      if (match.job_listing && !seenIds.has(match.job_listing.id)) {
        seenIds.add(match.job_listing.id)
        extra.push(match.job_listing)
      }
    }
    return [...allJobs, ...extra]
  }, [allJobs, recommended, myMatches, resumeIdFilter])

  // Keep a ref to all jobs (including matched/recommended) for use in callbacks
  const allJobsRef = useRef(allJobsWithMatches)
  allJobsRef.current = allJobsWithMatches

  // Categorize jobs based on match data
  const categorizedJobs = useMemo(() => {
    const applyNow: ExternalJobListingBrief[] = []
    const needSkills: ExternalJobListingBrief[] = []
    const notReady: ExternalJobListingBrief[] = []

    for (const job of allJobsWithMatches) {
      const match = analyzedJobs[job.id]
      if (!match) continue
      if (
        match.overall_score >= 70 &&
        (!match.missing_skills || match.missing_skills.length <= 1)
      ) {
        applyNow.push(job)
      } else if (
        match.missing_skills &&
        match.missing_skills.length > 0 &&
        match.overall_score >= 40
      ) {
        needSkills.push(job)
      } else if (match.overall_score < 40) {
        notReady.push(job)
      }
    }

    return { applyNow, needSkills, notReady }
  }, [allJobsWithMatches, analyzedJobs])

  // Apply search/filter params to categorized tabs client-side
  const filterBySearchParams = useCallback(
    (jobs: ExternalJobListingBrief[]) => {
      const hasFilters =
        searchParams.query ||
        searchParams.location ||
        searchParams.country ||
        searchParams.is_remote !== undefined ||
        searchParams.job_type ||
        searchParams.salary_min
      if (!hasFilters) return jobs

      return jobs.filter((job) => {
        if (searchParams.query) {
          const q = searchParams.query.toLowerCase()
          const terms = q.split(/\s+/).filter(Boolean)
          const title = job.title?.toLowerCase() || ''
          const company = job.company?.toLowerCase() || ''
          const location = job.location?.toLowerCase() || ''
          const haystack = `${title} ${company} ${location}`

          const phraseMatch = haystack.includes(q)
          const termMatch = terms.some((term) => haystack.includes(term))
          if (!phraseMatch && !termMatch) return false
        }
        if (searchParams.location) {
          const loc = searchParams.location.toLowerCase()
          if (!job.location?.toLowerCase().includes(loc)) return false
        }
        if (searchParams.country) {
          const c = searchParams.country
          if (!job.location?.endsWith(c) && job.location !== c) return false
        }
        if (searchParams.is_remote === true && !job.is_remote) return false
        if (searchParams.is_remote === false && job.is_remote) return false
        if (searchParams.job_type && job.job_type !== searchParams.job_type) return false
        if (searchParams.salary_min) {
          // Salary data is often partial; if missing, keep the job rather than treating it as 0.
          const maxComparableSalary = job.salary_max ?? job.salary_min
          if (
            maxComparableSalary !== null &&
            maxComparableSalary !== undefined &&
            maxComparableSalary < searchParams.salary_min
          ) {
            return false
          }
        }
        return true
      })
    },
    [searchParams],
  )

  const filteredApplyNow = filterBySearchParams(categorizedJobs.applyNow)
  const filteredNeedSkills = filterBySearchParams(categorizedJobs.needSkills)
  const filteredNotReady = filterBySearchParams(categorizedJobs.notReady)
  const filteredAllJobs = filterBySearchParams(allJobs)

  const matchedJobsByResume = useMemo(() => groupMatchesByDocumentId(myMatches?.items), [myMatches])
  const isVisibleResume = useCallback(
    (doc: NonNullable<typeof documents>[number]) =>
      doc.document_type === 'resume' &&
      doc.status !== 'failed' &&
      doc.status !== 'invalid_document',
    [],
  )
  const matchedResumeIds = useMemo(() => {
    return Object.keys(matchedJobsByResume)
      .map((key) => Number(key))
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => b - a)
  }, [matchedJobsByResume])
  const resumeById = useMemo(() => {
    const map = new Map<number, NonNullable<typeof documents>[number]>()
    for (const doc of documents ?? []) {
      if (!isVisibleResume(doc)) continue
      map.set(doc.id, doc)
    }
    return map
  }, [documents, isVisibleResume])
  const generatingMatchResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter(
        (doc) =>
          isVisibleResume(doc) &&
          (doc.job_matching_status === 'pending' || doc.job_matching_status === 'matching'),
      )
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [documents, isVisibleResume])
  const generatingResumeIdSet = useMemo(
    () => new Set(generatingMatchResumeIds),
    [generatingMatchResumeIds],
  )
  const completedResumeIds = useMemo(() => {
    return (documents ?? [])
      .filter((doc) => isVisibleResume(doc) && doc.job_matching_status === 'completed')
      .map((doc) => doc.id)
      .sort((a, b) => b - a)
  }, [documents, isVisibleResume])
  const existingResumeIds = useMemo(() => {
    return new Set((documents ?? []).filter((doc) => isVisibleResume(doc)).map((doc) => doc.id))
  }, [documents, isVisibleResume])
  const readyMatchedResumeIds = useMemo(() => {
    const effectiveResumeIds = Array.from(
      new Set(
        [...matchedResumeIds, ...completedResumeIds].filter((resumeId) =>
          existingResumeIds.has(resumeId),
        ),
      ),
    ).sort((a, b) => b - a)
    return effectiveResumeIds.filter((resumeId) => {
      if (generatingResumeIdSet.has(resumeId)) return false
      const resume = resumeById.get(resumeId)
      const hasLiveMatches = (matchedJobsByResume[resumeId]?.length ?? 0) > 0
      const isCompleted = resume?.job_matching_status === 'completed'
      return isCompleted || hasLiveMatches
    })
  }, [
    completedResumeIds,
    existingResumeIds,
    generatingResumeIdSet,
    matchedJobsByResume,
    matchedResumeIds,
    resumeById,
  ])
  const hasMatchedJobs = readyMatchedResumeIds.length > 0
  const matchedResumeNameById = useMemo(() => {
    const map: Record<number, string> = {}
    for (const resumeId of new Set([...generatingMatchResumeIds, ...readyMatchedResumeIds])) {
      map[resumeId] = resumeById.get(resumeId)?.original_filename || `Resume ${resumeId}`
    }
    return map
  }, [generatingMatchResumeIds, readyMatchedResumeIds, resumeById])
  const availableResumesForAnalysis = useMemo(
    () =>
      (documents ?? [])
        .filter((doc) => isVisibleResume(doc))
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .map((doc) => ({
          id: doc.id,
          label: doc.original_filename || `Resume ${doc.id}`,
        })),
    [documents, isVisibleResume],
  )

  useEffect(() => {
    setSelectedAnalyzeResumeId((prev) => {
      if (prev === null) return null
      return availableResumesForAnalysis.some((resume) => resume.id === prev) ? prev : null
    })
  }, [availableResumesForAnalysis])
  const selectedMatchedResumeOption =
    selectedMatchedResumeId !== null &&
    [...readyMatchedResumeIds, ...generatingMatchResumeIds].includes(selectedMatchedResumeId)
      ? `resume_${selectedMatchedResumeId}`
      : readyMatchedResumeIds.length > 0
        ? `resume_${readyMatchedResumeIds[0]}`
        : ''
  const selectedResumeDocument = useMemo(
    () =>
      selectedMatchedResumeId !== null
        ? ((documents ?? []).find(
            (doc) => doc.document_type === 'resume' && doc.id === selectedMatchedResumeId,
          ) ?? null)
        : null,
    [documents, selectedMatchedResumeId],
  )
  const selectedJobSearchRecord = useMemo(() => {
    if (!selectedJobSearchId) return null
    return (
      (jobSearchesData?.items ?? []).find((search) => search.id === selectedJobSearchId) ??
      selectedJobSearchData ??
      null
    )
  }, [jobSearchesData?.items, selectedJobSearchData, selectedJobSearchId])
  const selectedJobSearchResumeName = useMemo(() => {
    const resumeId = selectedJobSearchRecord?.document_id
    if (!resumeId) return null
    return resumeById.get(resumeId)?.original_filename || `Resume ${resumeId}`
  }, [resumeById, selectedJobSearchRecord?.document_id])

  useEffect(() => {
    if (selectedJobSearchId === null) {
      setCustomSearchStatusLines([])
      lastCustomStatusSignatureRef.current = null
    }
  }, [selectedJobSearchId])

  useEffect(() => {
    const status = selectedJobSearchRecord?.status ?? null
    const completed = selectedJobSearchRecord?.completed_jobs ?? null
    const total = selectedJobSearchRecord?.total_jobs ?? null
    const signature = status ? `${status}:${completed ?? 'n'}:${total ?? 'n'}` : null
    if (!signature || signature === lastCustomStatusSignatureRef.current) return
    lastCustomStatusSignatureRef.current = signature
    const line = buildAgentStatusLine({
      status,
      completed,
      total,
      completedLine: `I have discovered jobs from your selected filters and scored them against your resume ${selectedJobSearchResumeName || 'resume'}.`,
    })
    if (!line) return
    setCustomSearchStatusLines((prev) => appendRollingStatus(prev, line))
  }, [
    selectedJobSearchRecord?.completed_jobs,
    selectedJobSearchResumeName,
    selectedJobSearchRecord?.status,
    selectedJobSearchRecord?.total_jobs,
  ])

  useEffect(() => {
    if (selectedMatchedResumeId === null) {
      setResumeStatusLines([])
      lastResumeStatusSignatureRef.current = null
    }
  }, [selectedMatchedResumeId])

  useEffect(() => {
    const status = (selectedResumeDocument?.job_matching_status as JobSearchStatus | null) ?? null
    const completed = selectedResumeDocument?.job_matching_completed_count ?? null
    const total = selectedResumeDocument?.job_matching_total ?? null
    const signature = status ? `${status}:${completed ?? 'n'}:${total ?? 'n'}` : null
    if (!signature || signature === lastResumeStatusSignatureRef.current) return
    lastResumeStatusSignatureRef.current = signature
    const line = buildAgentStatusLine({
      status,
      completed,
      total,
      completedLine:
        'I have discovered jobs from your resume profile and scored them against your resume.',
    })
    if (!line) return
    setResumeStatusLines((prev) => appendRollingStatus(prev, line))
  }, [
    selectedResumeDocument?.job_matching_completed_count,
    selectedResumeDocument?.job_matching_status,
    selectedResumeDocument?.job_matching_total,
  ])
  const selectedResumeMatches = useMemo(
    () =>
      selectedMatchedResumeId !== null ? (matchedJobsByResume[selectedMatchedResumeId] ?? []) : [],
    [matchedJobsByResume, selectedMatchedResumeId],
  )
  const selectedResumeMatchByJobId = useMemo(() => {
    const map: Record<number, ExternalJobMatchWithJob> = {}
    for (const match of selectedResumeMatches) {
      map[match.job_listing_id] = match
    }
    return map
  }, [selectedResumeMatches])
  const categorizedSelectedResumeJobs = useMemo(() => {
    const applyNow: ExternalJobListingBrief[] = []
    const needSkills: ExternalJobListingBrief[] = []
    const notReady: ExternalJobListingBrief[] = []

    for (const match of selectedResumeMatches) {
      const job = match.job_listing
      if (!job) continue
      if (
        match.overall_score >= 70 &&
        (!match.missing_skills || match.missing_skills.length <= 1)
      ) {
        applyNow.push(job)
      } else if (
        match.missing_skills &&
        match.missing_skills.length > 0 &&
        match.overall_score >= 40
      ) {
        needSkills.push(job)
      } else if (match.overall_score < 40) {
        notReady.push(job)
      }
    }

    return { applyNow, needSkills, notReady }
  }, [selectedResumeMatches])

  const selectedJobSearchMatches = useMemo(
    () => selectedJobSearchData?.matches ?? [],
    [selectedJobSearchData],
  )
  const selectedGeneratedPreviewJobs = useMemo(
    () => (selectedJobSearchId ? (generatedPreviewJobsBySearchId[selectedJobSearchId] ?? []) : []),
    [generatedPreviewJobsBySearchId, selectedJobSearchId],
  )
  const selectedJobSearchEffectiveJobs = useMemo(() => {
    const jobs =
      selectedJobSearchMatches.length > 0
        ? selectedJobSearchMatches
            .map((match) => match.job_listing)
            .filter((job): job is ExternalJobListingBrief => Boolean(job))
        : selectedGeneratedPreviewJobs

    const seen = new Set<number>()
    return jobs.filter((job) => {
      if (seen.has(job.id)) return false
      seen.add(job.id)
      return true
    })
  }, [selectedGeneratedPreviewJobs, selectedJobSearchMatches])
  const selectedJobSearchMatchByJobId = useMemo(() => {
    const map: Record<number, ExternalJobMatchWithJob> = {}
    for (const match of selectedJobSearchMatches) {
      map[match.job_listing_id] = match
    }
    return map
  }, [selectedJobSearchMatches])
  const categorizedSelectedJobSearchJobs = useMemo(() => {
    if (selectedJobSearchMatches.length === 0) {
      return { applyNow: [], needSkills: [], notReady: [], all: selectedGeneratedPreviewJobs }
    }
    const applyNow: ExternalJobListingBrief[] = []
    const needSkills: ExternalJobListingBrief[] = []
    const notReady: ExternalJobListingBrief[] = []

    for (const match of selectedJobSearchMatches) {
      const job = match.job_listing
      if (!job) continue
      if (
        match.overall_score >= 70 &&
        (!match.missing_skills || match.missing_skills.length <= 1)
      ) {
        applyNow.push(job)
      } else if (
        match.missing_skills &&
        match.missing_skills.length > 0 &&
        match.overall_score >= 40
      ) {
        needSkills.push(job)
      } else if (match.overall_score < 40) {
        notReady.push(job)
      }
    }

    return { applyNow, needSkills, notReady, all: selectedJobSearchEffectiveJobs }
  }, [selectedGeneratedPreviewJobs, selectedJobSearchEffectiveJobs, selectedJobSearchMatches])

  const tabs: { key: JobTab; label: string; count?: number | undefined }[] =
    selectedJobSearchId !== null
      ? [
          { key: 'all', label: 'All', count: selectedJobSearchEffectiveJobs.length || undefined },
          {
            key: 'apply_now',
            label: 'Apply Now',
            count: categorizedSelectedJobSearchJobs.applyNow.length || undefined,
          },
          {
            key: 'need_skills',
            label: 'Need Skills for',
            count: categorizedSelectedJobSearchJobs.needSkills.length || undefined,
          },
          {
            key: 'not_ready',
            label: 'Not Ready',
            count: categorizedSelectedJobSearchJobs.notReady.length || undefined,
          },
        ]
      : selectedMatchedResumeId !== null
        ? [
            { key: 'all', label: 'All', count: selectedResumeMatches.length || undefined },
            {
              key: 'apply_now',
              label: 'Apply Now',
              count: categorizedSelectedResumeJobs.applyNow.length || undefined,
            },
            {
              key: 'need_skills',
              label: 'Need Skills for',
              count: categorizedSelectedResumeJobs.needSkills.length || undefined,
            },
            {
              key: 'not_ready',
              label: 'Not Ready',
              count: categorizedSelectedResumeJobs.notReady.length || undefined,
            },
          ]
        : [
            { key: 'all', label: 'All', count: filteredAllJobs.length || undefined },
            { key: 'apply_now', label: 'Apply Now', count: filteredApplyNow.length || undefined },
            {
              key: 'need_skills',
              label: 'Need Skills for',
              count: filteredNeedSkills.length || undefined,
            },
            { key: 'not_ready', label: 'Not Ready', count: filteredNotReady.length || undefined },
          ]

  const displayJobs = useMemo(() => {
    if (selectedJobSearchId !== null) {
      let jobs: ExternalJobListingBrief[]
      switch (activeTab) {
        case 'apply_now':
          jobs = categorizedSelectedJobSearchJobs.applyNow
          break
        case 'need_skills':
          jobs = categorizedSelectedJobSearchJobs.needSkills
          break
        case 'not_ready':
          jobs = categorizedSelectedJobSearchJobs.notReady
          break
        default:
          jobs = categorizedSelectedJobSearchJobs.all
      }
      if (sortOrder === 'none') return jobs
      return [...jobs].sort((a, b) => {
        const scoreA = selectedJobSearchMatchByJobId[a.id]?.overall_score ?? -1
        const scoreB = selectedJobSearchMatchByJobId[b.id]?.overall_score ?? -1
        return sortOrder === 'score_desc' ? scoreB - scoreA : scoreA - scoreB
      })
    }

    if (selectedMatchedResumeId !== null) {
      let jobs: ExternalJobListingBrief[]
      switch (activeTab) {
        case 'apply_now':
          jobs = categorizedSelectedResumeJobs.applyNow
          break
        case 'need_skills':
          jobs = categorizedSelectedResumeJobs.needSkills
          break
        case 'not_ready':
          jobs = categorizedSelectedResumeJobs.notReady
          break
        default:
          jobs = selectedResumeMatches
            .map((match) => match.job_listing)
            .filter((job): job is ExternalJobListingBrief => Boolean(job))
      }
      if (sortOrder === 'none') return jobs
      return [...jobs].sort((a, b) => {
        const scoreA = selectedResumeMatchByJobId[a.id]?.overall_score ?? -1
        const scoreB = selectedResumeMatchByJobId[b.id]?.overall_score ?? -1
        return sortOrder === 'score_desc' ? scoreB - scoreA : scoreA - scoreB
      })
    }

    let jobs: ExternalJobListingBrief[]
    switch (activeTab) {
      case 'apply_now':
        jobs = filterBySearchParams(categorizedJobs.applyNow)
        break
      case 'need_skills':
        jobs = filterBySearchParams(categorizedJobs.needSkills)
        break
      case 'not_ready':
        jobs = filterBySearchParams(categorizedJobs.notReady)
        break
      default:
        jobs = filteredAllJobs
    }
    if (sortOrder === 'none') return jobs
    return [...jobs].sort((a, b) => {
      const scoreA = analyzedJobs[a.id]?.overall_score ?? -1
      const scoreB = analyzedJobs[b.id]?.overall_score ?? -1
      return sortOrder === 'score_desc' ? scoreB - scoreA : scoreA - scoreB
    })
  }, [
    selectedJobSearchId,
    categorizedSelectedJobSearchJobs,
    selectedJobSearchMatchByJobId,
    selectedMatchedResumeId,
    activeTab,
    categorizedSelectedResumeJobs,
    selectedResumeMatches,
    sortOrder,
    selectedResumeMatchByJobId,
    filteredAllJobs,
    categorizedJobs,
    analyzedJobs,
    filterBySearchParams,
  ])
  const isSelectedJobSearchScoring =
    selectedJobSearchId !== null &&
    (selectedJobSearchRecord?.status === 'pending' ||
      selectedJobSearchRecord?.status === 'matching')
  const isSelectedResumeScoring =
    selectedMatchedResumeId !== null &&
    (selectedResumeDocument?.job_matching_status === 'pending' ||
      selectedResumeDocument?.job_matching_status === 'matching')
  const showScoredJobsComingSoonState =
    displayJobs.length === 0 && (isSelectedJobSearchScoring || isSelectedResumeScoring)

  useEffect(() => {
    if (preferenceHydratedRef.current) return
    if (!preferences) return

    setSearchParams((prev) => ({
      ...prev,
      ...(preferences.job_title ? { query: preferences.job_title } : {}),
      ...(preferences.location ? { location: preferences.location } : {}),
      ...(preferences.is_remote !== null ? { is_remote: preferences.is_remote } : {}),
      ...(preferences.employment_type ? { job_type: preferences.employment_type as JobType } : {}),
      ...(preferences.compensation_min ? { salary_min: preferences.compensation_min } : {}),
    }))
    preferenceHydratedRef.current = true
  }, [preferences])

  useEffect(() => {
    if (!preferenceHydratedRef.current) return

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current)
    }

    persistTimerRef.current = setTimeout(() => {
      const nextPayload = {
        job_title: searchParams.query ?? null,
        location: searchParams.location ?? null,
        is_remote: searchParams.is_remote ?? null,
        employment_type: (searchParams.job_type as
          | 'full_time'
          | 'part_time'
          | 'contract'
          | undefined)
          ? (searchParams.job_type as 'full_time' | 'part_time' | 'contract')
          : null,
        compensation_min: searchParams.salary_min ?? null,
      }

      const hasAnyValue = Object.values(nextPayload).some((value) => value !== null && value !== '')
      if (!hasAnyValue) return

      if (preferences === null) {
        createPreferences.mutate(nextPayload)
        return
      }

      if (
        nextPayload.job_title !== preferences?.job_title ||
        nextPayload.location !== preferences?.location ||
        nextPayload.is_remote !== preferences?.is_remote ||
        nextPayload.employment_type !== preferences?.employment_type ||
        nextPayload.compensation_min !== preferences?.compensation_min
      ) {
        updatePreferences.mutate(nextPayload)
      }
    }, 800)

    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
      }
    }
  }, [searchParams, preferences, updatePreferences, createPreferences])

  // Close the details panel when navigation changes route/query within the app.
  useEffect(() => {
    const routeSignature = `${location.pathname}${location.search}`
    if (previousRouteRef.current === null) {
      previousRouteRef.current = routeSignature
      return
    }
    if (previousRouteRef.current === routeSignature) return
    previousRouteRef.current = routeSignature
    setSelectedJobId(null)
    setSelectedMatchData(null)
  }, [location.pathname, location.search])

  useEffect(() => {
    if (selectedJobSearchId !== null) {
      trackEvent('result_set_opened', { source_type: 'custom_search', set_id: selectedJobSearchId })
      return
    }
    if (selectedMatchedResumeId !== null) {
      trackEvent('result_set_opened', {
        source_type: 'resume_scan',
        resume_id: selectedMatchedResumeId,
      })
    }
  }, [selectedJobSearchId, selectedMatchedResumeId])

  useEffect(() => {
    const closeDetailsPanel = () => {
      setSelectedJobId(null)
      setSelectedMatchData(null)
    }

    window.addEventListener('pelgo:sidebar-navigation', closeDetailsPanel)
    return () => window.removeEventListener('pelgo:sidebar-navigation', closeDetailsPanel)
  }, [])

  return (
    <div
      className={cn('h-full bg-[#F3F4F6] dark:bg-background relative', isMobile && 'flex flex-col')}
    >
      <div
        className={cn('p-4 md:p-6', isMobile ? 'flex-1 overflow-y-auto' : 'h-full overflow-auto')}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold flex items-center gap-2.5">
            <Briefcase className="h-6 w-6 text-foreground" />
            {isMobile ? 'Jobs' : 'Opportunities'}
          </h1>
        </div>
        {isMobile && (hasMatchedJobs || customSearchSets.length > 0) && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-white p-1">
            <button
              type="button"
              onClick={() => navigate('/opportunities')}
              className={cn(
                'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                selectedMatchedResumeId === null && selectedJobSearchId === null
                  ? 'bg-sky-50 text-sky-700'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Launch job search agent
            </button>
            <button
              type="button"
              onClick={() => {
                if (readyMatchedResumeIds.length === 0) return
                navigate(`/opportunities?resume_id=${readyMatchedResumeIds[0]}`)
              }}
              className={cn(
                'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                selectedMatchedResumeId !== null
                  ? 'bg-sky-50 text-sky-700'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Resume Scan Agents
            </button>
            {mobileJobSearchTabTargetId !== null && (
              <button
                type="button"
                onClick={() => {
                  navigate(`/opportunities?job_search_id=${mobileJobSearchTabTargetId}`)
                }}
                className={cn(
                  'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  selectedJobSearchId !== null
                    ? 'bg-sky-50 text-sky-700'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Job Search Agents
              </button>
            )}
          </div>
        )}

        {selectedMatchedResumeId === null && selectedJobSearchId === null ? (
          <SearchCriteria
            params={searchParams}
            onParamsChange={setSearchParams}
            resumes={availableResumesForAnalysis}
            selectedResumeId={selectedAnalyzeResumeId}
            onSelectedResumeIdChange={setSelectedAnalyzeResumeId}
            onGenerateMatches={() => {
              const boundedJobs = filteredAllJobs.slice(0, JOB_SEARCH_AGENT_MAX_SCAN_RESULTS)
              const capturedJobIds = boundedJobs.map((job) => job.id)
              if (capturedJobIds.length === 0) return
              const capturedJobs = [...boundedJobs]
              const parsedChatId = chatId ? Number(chatId) : undefined
              const criteriaTitle = buildSearchCriteriaTitle(searchParams)
              const jobSearchPayload = {
                ...(selectedAnalyzeResumeId ? { document_id: selectedAnalyzeResumeId } : {}),
                job_title: criteriaTitle,
                ...(searchParams.location ? { location: searchParams.location } : {}),
                ...(searchParams.job_type ? { job_type: searchParams.job_type } : {}),
                ...(searchParams.is_remote !== undefined
                  ? { is_remote: searchParams.is_remote }
                  : {}),
                ...(parsedChatId && Number.isFinite(parsedChatId) ? { chat_id: parsedChatId } : {}),
                job_ids: capturedJobIds,
              }
              createJobSearch.mutate(jobSearchPayload, {
                onSuccess: (search) => {
                  setSelectedAnalyzeResumeId(null)
                  setGeneratedPreviewJobsBySearchId((prev) => ({
                    ...prev,
                    [search.id]: capturedJobs,
                  }))
                  setActiveTab('all')
                  navigate(`/opportunities?job_search_id=${search.id}`)
                },
              })
            }}
            onReset={() => {
              setSearchParams({})
              setSelectedAnalyzeResumeId(null)
            }}
            isGenerating={isLoading || createJobSearch.isPending}
            shortlistCount={filteredAllJobs.length}
          />
        ) : selectedJobSearchId !== null ? (
          <Card className="mb-6 bg-sky-100/60 border-sky-300/70">
            <CardContent className="p-4">
              <div className="mt-2 min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="h-4 w-4 text-sky-500" />
                  <span>Job Search Agent</span>
                  {(selectedJobSearchRecord?.status === 'pending' ||
                    selectedJobSearchRecord?.status === 'matching') && (
                    <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                  )}
                </div>
                <ResultSetStatusWindow
                  lines={customSearchStatusLines}
                  isTyping={
                    selectedJobSearchRecord?.status === 'pending' ||
                    selectedJobSearchRecord?.status === 'matching'
                  }
                  resumeName={selectedJobSearchResumeName}
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Select
                    value={selectedJobSearchId ? `search_${selectedJobSearchId}` : ''}
                    onValueChange={(value) => {
                      const searchId = Number(value.replace('search_', ''))
                      if (!Number.isFinite(searchId)) return
                      navigate(`/opportunities?job_search_id=${searchId}`)
                    }}
                  >
                    <SelectTrigger className="h-8 min-w-[220px] max-w-full bg-white border-sky-200 text-sky-700">
                      <SelectValue placeholder="Select generated search" />
                    </SelectTrigger>
                    <SelectContent>
                      {customSearchSets.map((search) => (
                        <SelectItem key={search.id} value={`search_${search.id}`}>
                          {search.job_title || 'Launch job search agent'}{' '}
                          {search.status === 'matching' ? '- Working...' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ResultSetHelpFooter
                  onRequestHumanHelp={handleRequestHumanCoach}
                  disabled={!chatId}
                />
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6 bg-sky-100/60 border-sky-300/70">
            <CardContent className="p-4">
              <div className="mt-2 min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Sparkles className="h-4 w-4 text-sky-500" />
                  <span>Resume Scan Agent</span>
                  {(selectedResumeDocument?.job_matching_status === 'pending' ||
                    selectedResumeDocument?.job_matching_status === 'matching') && (
                    <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                  )}
                </div>
                <ResumeSetStatusWindow
                  lines={resumeStatusLines}
                  isTyping={
                    selectedResumeDocument?.job_matching_status === 'pending' ||
                    selectedResumeDocument?.job_matching_status === 'matching'
                  }
                />
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Select
                    value={selectedMatchedResumeOption}
                    onValueChange={(value) => {
                      const resumeId = Number(value.replace('resume_', ''))
                      if (!Number.isFinite(resumeId)) return
                      const allowedIds = new Set([
                        ...readyMatchedResumeIds,
                        ...generatingMatchResumeIds,
                      ])
                      if (!allowedIds.has(resumeId)) return
                      navigate(`/opportunities?resume_id=${resumeId}`)
                    }}
                  >
                    <SelectTrigger className="h-8 min-w-[220px] max-w-full bg-white border-sky-200 text-black">
                      <SelectValue placeholder="Select resume" />
                    </SelectTrigger>
                    <SelectContent>
                      {generatingMatchResumeIds.map((resumeId) => {
                        const resume = resumeById.get(resumeId)
                        const total = resume?.job_matching_total ?? null
                        const completed = resume?.job_matching_completed_count ?? null
                        const progress =
                          total && total > 0 && completed !== null
                            ? Math.max(0, Math.min(100, Math.round((completed / total) * 100)))
                            : null
                        const loadingText =
                          progress !== null ? `Working... ${progress}%` : 'Working...'
                        return (
                          <SelectItem key={resumeId} value={`resume_${resumeId}`}>
                            {matchedResumeNameById[resumeId]} - {loadingText}
                          </SelectItem>
                        )
                      })}
                      {readyMatchedResumeIds.map((resumeId) => (
                        <SelectItem key={resumeId} value={`resume_${resumeId}`}>
                          {matchedResumeNameById[resumeId]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <ResultSetHelpFooter
                  onRequestHumanHelp={handleRequestHumanCoach}
                  disabled={!chatId}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs + Sort/Filter */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 min-w-0 overflow-x-auto scrollbar-none">
            <div className="flex gap-1 flex-nowrap">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={cn(
                    'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap font-medium',
                    activeTab === tab.key
                      ? 'bg-sky-50 text-sky-700'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <span
                      className={cn(
                        'inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs font-semibold',
                        activeTab === tab.key
                          ? 'bg-sky-50 border border-sky-300 text-sky-700'
                          : 'bg-white text-muted-foreground',
                      )}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className={cn('text-muted-foreground', sortOrder !== 'none' && 'text-foreground')}
              onClick={() => {
                setSortOrder((prev) => {
                  if (prev === 'none') return 'score_desc'
                  if (prev === 'score_desc') return 'score_asc'
                  return 'none'
                })
              }}
            >
              {sortOrder === 'score_asc' ? (
                <SortAsc className="h-4 w-4 mr-1" />
              ) : (
                <SortDesc className="h-4 w-4 mr-1" />
              )}
              <span className="hidden sm:inline">
                {sortOrder === 'none'
                  ? 'Sort'
                  : sortOrder === 'score_desc'
                    ? 'Match: High'
                    : 'Match: Low'}
              </span>
            </Button>
          </div>
        </div>

        {/* Job Listings + Detail Panel */}
        {selectedMatchedResumeId === null && selectedJobSearchId === null && isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : showScoredJobsComingSoonState ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">Scored jobs will appear here soon.</p>
            </CardContent>
          </Card>
        ) : displayJobs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                {activeTab === 'all'
                  ? 'No jobs found. Try adjusting your search criteria.'
                  : 'No jobs in this category yet. Try checking your match on more jobs.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {displayJobs.map((job) => {
              const selectedResumeMatch = selectedResumeMatchByJobId[job.id]
              const selectedGeneratedMatch = selectedJobSearchMatchByJobId[job.id]
              const searchScoringInProgress =
                selectedJobSearchId !== null && selectedJobSearchRecord?.status === 'matching'
              const resumeScoringInProgress =
                selectedMatchedResumeId !== null &&
                selectedResumeDocument?.job_matching_status === 'matching'
              const showCardAnalyzingState =
                (searchScoringInProgress && !selectedGeneratedMatch) ||
                (resumeScoringInProgress && !selectedResumeMatch)
              const matchData =
                selectedJobSearchId !== null
                  ? selectedGeneratedMatch
                    ? toMatchResultData(selectedGeneratedMatch)
                    : (analyzedJobs[job.id] ?? null)
                  : selectedMatchedResumeId !== null
                    ? selectedResumeMatch
                      ? toMatchResultData(selectedResumeMatch)
                      : null
                    : (analyzedJobs[job.id] ?? null)
              const hasLearningPath = jobIdsWithLearningPath.has(job.id)
              const applyRun = applyResumeRunsByJobId[job.id]
              const atsTailoredReady =
                applyRun?.status === 'ready' || applyRun?.status === 'ready_reanalysis_failed'

              const displayMatch = mergeTailoredReanalysisIntoMatch(matchData, applyRun)
              const atsBoostPercent = resolveResumeBoostPercent(applyRun)

              const generatingForThisJob = jobHasGeneratingTopics(job.id, topicsData?.items)
              const anyLessonsGenerating = hasAnyGeneratingTopics(topicsData?.items)
              const learnAllBlockedElsewhere = anyLessonsGenerating && !generatingForThisJob
              const canLearnAll =
                !hasLearningPath &&
                displayMatch &&
                displayMatch.overall_score > 0 &&
                displayMatch.missing_skills &&
                displayMatch.missing_skills.length > 0

              return (
                <JobCard
                  key={job.id}
                  job={job}
                  match={displayMatch}
                  onViewDetails={
                    selectedJobSearchId !== null
                      ? () => {
                          setSelectedJobId(job.id)
                          if (!selectedGeneratedMatch) {
                            setSelectedMatchData(null)
                            return
                          }
                          setSelectedMatchData(toMatchResultData(selectedGeneratedMatch))
                        }
                      : selectedMatchedResumeId !== null
                        ? () => {
                            if (!selectedResumeMatch) return
                            setSelectedJobId(selectedResumeMatch.job_listing_id)
                            setSelectedMatchData(toMatchResultData(selectedResumeMatch))
                          }
                        : handleViewDetails
                  }
                  onAnalyzeMatch={handleAnalyzeMatch}
                  analyzeResumes={availableResumesForAnalysis}
                  {...(generatingForThisJob
                    ? { learnAllCurating: true }
                    : hasLearningPath
                      ? {
                          onLearnAll: () => navigate('/learn'),
                          learnAllLabel: 'Continue learning',
                          learnAllForceShow: true,
                        }
                      : canLearnAll && displayMatch && atsTailoredReady
                        ? {
                            onLearnAll: () => handleLearnAllFromJob(job, displayMatch),
                            learnAllPending: learnAllPendingJobId === job.id,
                            learnAllBlockedWhileGeneratingElsewhere: learnAllBlockedElsewhere,
                            learnAllBlockedTooltip: LEARN_ALL_BLOCKED_WHILE_GENERATING_TOOLTIP,
                            learnAllError:
                              learnAllListError?.jobId === job.id
                                ? learnAllListError.message
                                : null,
                            learnAllLabel: 'Learn all skills',
                          }
                        : {})}
                  {...(chatId ? { onAskAbout: handleAskAbout } : {})}
                  onGenerateResume={handleGenerateResume}
                  onRetryGenerateResume={handleRetryGenerateResume}
                  onViewGeneratedResume={handleViewGeneratedResume}
                  onDownloadGeneratedResume={handleDownloadGeneratedResume}
                  generateResumeStatus={applyRun?.status ?? null}
                  generateResumeDelta={atsBoostPercent}
                  generateResumeError={applyRun?.error_message ?? null}
                  isAnalyzing={analyzingJobIds.has(job.id) || showCardAnalyzingState}
                />
              )
            })}

            {/* Infinite scroll sentinel */}
            {selectedMatchedResumeId === null &&
              selectedJobSearchId === null &&
              activeTab === 'all' && (
                <>
                  <div ref={sentinelRef} className="h-1" />
                  {isFetchingNextPage && (
                    <div className="flex justify-center mt-6">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </>
              )}
          </div>
        )}
      </div>

      {/* Job Detail Panel (desktop) / Dialog + Drawer (mobile) */}
      {!isMobile && (
        <JobDetailPanel
          jobId={selectedJobId}
          open={selectedJobId !== null}
          onClose={() => {
            setSelectedJobId(null)
            setSelectedMatchData(null)
          }}
          initialMatchData={detailPanelMatchData}
          onAnalyzeMatch={handleAnalyzeMatch}
          isAnalyzing={selectedJobId !== null && analyzingJobIds.has(selectedJobId)}
          atsTailoredReady={detailAtsTailoredReady}
          analyzeResumes={availableResumesForAnalysis}
          onGenerateResume={handleGenerateResume}
          onRetryGenerateResume={handleRetryGenerateResume}
          onDownloadGeneratedResume={handleDownloadGeneratedResume}
          generateResumeStatus={selectedJobApplyRun?.status ?? null}
          generateResumeDelta={selectedJobAtsBoostPercent}
          generateResumeError={selectedJobApplyRun?.error_message ?? null}
        />
      )}
      {isMobile && (
        <>
          <JobDetailDialog
            jobId={selectedJobId}
            open={selectedJobId !== null}
            onOpenChange={(open) => {
              if (!open) {
                setSelectedJobId(null)
                setSelectedMatchData(null)
              }
            }}
            initialMatchData={detailPanelMatchData}
            onAnalyzeMatch={handleAnalyzeMatch}
            isAnalyzing={selectedJobId !== null && analyzingJobIds.has(selectedJobId)}
            atsTailoredReady={detailAtsTailoredReady}
            analyzeResumes={availableResumesForAnalysis}
            onGenerateResume={handleGenerateResume}
            onRetryGenerateResume={handleRetryGenerateResume}
            onDownloadGeneratedResume={handleDownloadGeneratedResume}
            generateResumeStatus={selectedJobApplyRun?.status ?? null}
            generateResumeDelta={selectedJobAtsBoostPercent}
            generateResumeError={selectedJobApplyRun?.error_message ?? null}
          />
          {/*
          <CoachHalfDrawer
            message={coachMessage}
            actions={[{ label: 'Update Preferences', onClick: () => navigate('/chat') }]}
          />
          */}
        </>
      )}
    </div>
  )
}
